import React, { useState } from 'react'
import { CircularProgress } from '@mui/material'
import StyledButton from '@/components/Button/Button'
import PasswordInput from '@/components/Common/PasswordInput'
import Popup from '@/components/Common/Popup'
import useChangePassword from '../services/hooks/usePostChangePassword'
import useValidation from '@/hooks/useValidation'

interface ValidationRule {
  required?: boolean
  message: string
  pattern?:
    | RegExp
    | ((value: string, fields: Record<string, string>) => boolean)
}

const ChangePassword = () => {
  const inputValidationRules: Record<string, ValidationRule[]> = {
    newPassword: [
      { required: true, message: 'Please enter your new password.' },
      {
        pattern: /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}$/,
        message:
          'New Password must contain at least 8 characters, including uppercase, lowercase letters, and a number.',
      },
    ],
    oldPassword: [
      { required: true, message: 'Please enter your old password.' },
      {
        pattern: /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}$/,
        message:
          'Old Password must contain at least 8 characters, including uppercase, lowercase letters, and a number.',
      },
    ],
    confirmNewPassword: [
      { required: true, message: 'Please enter your confirm new password.' },
      {
        pattern: (value: string, fields: Record<string, string>) =>
          value === fields.newPassword,
        message: 'New passwords do not match.',
      },
    ],
  }

  const [confirmNewPassword, setConfirmNewPassword] = useState<string>('')
  const [errorMsg, setErrorMsg] = useState<string>('')
  const [newPassword, setNewPassword] = useState<string>('')
  const [oldPassword, setOldPassword] = useState<string>('')
  const [showPopup, setShowPopup] = useState<boolean>(false)

  const { errors, validateInputs } = useValidation(
    {
      oldPassword,
      newPassword,
      confirmNewPassword,
    },
    inputValidationRules,
  )

  const { mutate: resetPassword, isPending } = useChangePassword()

  const onChangeConfirmNewPassword = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setConfirmNewPassword(event.target.value)
  }

  const onChangeNewPassword = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewPassword(event.target.value)
  }

  const onChangeOldPassword = (event: React.ChangeEvent<HTMLInputElement>) => {
    setOldPassword(event.target.value)
  }

  const handleClickDone = () => {
    setShowPopup(false)
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!validateInputs()) {
      return
    }
    if (oldPassword !== '' && newPassword !== '' && confirmNewPassword !== '') {
      resetPassword(
        {
          oldPassword,
          newPassword,
          confirmNewPassword,
        },
        {
          onSuccess: () => {
            setShowPopup(true)
          },
          onError: (error) => {
            setErrorMsg(
              error.message || 'Something went wrong. Please try again.',
            )
          },
        },
      )
    }
  }

  return (
    <>
      <form onSubmit={handleSubmit}>
        <div className="mt-6 flex flex-col gap-4 w-full md:w-2/3">
          <PasswordInput
            value={oldPassword}
            onChange={onChangeOldPassword}
            label="Old Password"
            className="password-field"
            error={!!errors.oldPassword || errorMsg === 'Invalid password'}
            helperText={errors.oldPassword || errorMsg}
          />
          <PasswordInput
            value={newPassword}
            onChange={onChangeNewPassword}
            label="New Password"
            className="password-field"
            error={
              !!errors.newPassword ||
              errorMsg === 'New password cannot be same as old password' ||
              errorMsg.includes('uppercase')
            }
            helperText={errors.newPassword || errorMsg}
          />
          <PasswordInput
            value={confirmNewPassword}
            onChange={onChangeConfirmNewPassword}
            label="Confirm Password"
            className="password-field"
            error={!!errors.confirmNewPassword}
            helperText={errors.confirmNewPassword}
          />
          <StyledButton
            type="submit"
            className="h-14 w-52"
            disabled={
              oldPassword === '' ||
              newPassword === '' ||
              confirmNewPassword === '' ||
              isPending
            }
          >
            {isPending ? (
              <CircularProgress className="!text-[#181a21] !text-2xl" />
            ) : (
              'Update Password'
            )}
          </StyledButton>
        </div>
      </form>
      {showPopup && (
        <Popup
          title={
            <>
              Password Reset <br /> Successfully
            </>
          }
          message="Your password has been reset. You can now log in with your new password."
          buttonText="Done"
          onButtonClick={handleClickDone}
        />
      )}
    </>
  )
}

export default ChangePassword
